import React, { useState } from 'react';
import axios from 'axios';
import { Flag, Plus, Trash } from '@phosphor-icons/react';
import { notify } from '../../lib/notify';
import { inr } from './ProfitWaterfall';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fyLabel = (y) => `FY ${y}-${String((Number(y) + 1) % 100).padStart(2, '0')}`;
const currentFy = () => { const d = new Date(); return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; };

// Yearly net-profit targets (Apr–Mar), split evenly per month. President edits; VP views.
export default function TargetPanel({ targets, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState(targets);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => setRows(targets), [targets]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profitability/targets`, { yearly_targets: rows.map((r) => ({ fy_start_year: Number(r.fy_start_year), amount: Number(r.amount || 0) })) });
      notify('Targets saved', 'success'); setEditing(false); onSaved();
    } catch (e) {
      notify(e.response?.data?.detail || 'Could not save targets', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="target-panel">
      <Flag size={16} className="text-[#00a69c]" />
      {!editing ? (
        <>
          <span className="text-[#3B5A54]" data-testid="target-summary">
            {targets.length ? targets.map((t) => `${fyLabel(t.fy_start_year)}: ${inr(t.amount)} (${inr(t.amount / 12)}/month)`).join(' · ') : 'No net-profit target set'}
          </span>
          {canEdit && <button type="button" onClick={() => setEditing(true)} className="text-xs font-semibold text-[#00a69c] hover:underline" data-testid="target-edit-btn">{targets.length ? 'Edit targets' : 'Set yearly target'}</button>}
        </>
      ) : (
        <>
          {rows.map((r, i) => (
            <span key={i} className="inline-flex items-center gap-1.5" data-testid={`target-row-${i}`}>
              <select value={r.fy_start_year} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, fy_start_year: e.target.value } : x)))}
                className="px-2 py-1.5 border border-[#D5E8E4] rounded-lg text-sm" data-testid={`target-fy-${i}`}>
                {[-2, -1, 0, 1, 2].map((o) => { const y = currentFy() + o; return <option key={y} value={y}>{fyLabel(y)}</option>; })}
              </select>
              <input type="number" value={r.amount} placeholder="₹ yearly net profit" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                className="w-40 px-2 py-1.5 border border-[#D5E8E4] rounded-lg text-sm" data-testid={`target-amount-${i}`} />
              <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="text-rose-600" data-testid={`target-remove-${i}`}><Trash size={14} /></button>
            </span>
          ))}
          <button type="button" onClick={() => setRows([...rows, { fy_start_year: currentFy(), amount: '' }])} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00a69c]" data-testid="target-add-btn"><Plus size={12} /> Add year</button>
          <button type="button" onClick={save} disabled={saving} className="px-3 py-1.5 rounded-lg bg-[#00a69c] text-white text-xs font-semibold disabled:opacity-60" data-testid="target-save-btn">{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" onClick={() => { setEditing(false); setRows(targets); }} className="px-3 py-1.5 rounded-lg border border-[#D5E8E4] text-xs" data-testid="target-cancel-btn">Cancel</button>
        </>
      )}
    </div>
  );
}
